<div class="page-container">
    <!-- Tabs -->
    <div class="page-tabs-row">
        <div class="page-tabs">
            @for (tab of tabs; track tab.key) {
                @if (tab.key !== 'add') {
                    <button
                        class="page-tab"
                        [class.active]="activeTab === tab.key"
                        (click)="changeTab(tab.key)">
                        {{ tab.title }} ({{ tab.key === 'current' ? currentCaseCount : completeCaseCount }})
                    </button>
                }
            }
        </div>
        @if (!isAdmin) {
            <button class="page-tab page-tab-add" (click)="changeTab('add')">
                <hugeicons-icon [icon]="addIcon" [size]="16" color="currentColor"></hugeicons-icon>
                New Case
            </button>
        }
    </div>

    <!-- Filters -->
    <div class="filters-bar">
        <div class="search-input">
            <hugeicons-icon [icon]="searchIcon" [size]="16" class="search-icon"></hugeicons-icon>
            <input type="text" placeholder="Search by name, case code..." [value]="searchTerm" (input)="onSearchInput($event)">
        </div>
        <div class="date-filter">
            <app-date-picker placeholder="Filter by date" [showLabel]="false" [(ngModel)]="filterDate" [highlightDates]="caseDates" (ngModelChange)="onFilterChange()"></app-date-picker>
        </div>
        <div class="select-filter">
            <app-select
                [options]="statusOptions"
                placeholder="All Statuses"
                [searchable]="false"
                [(ngModel)]="filterStatus"
                (ngModelChange)="onFilterChange()">
            </app-select>
        </div>
        @if (searchTerm || filterDate || filterStatus) {
            <button class="filters-clear-btn" (click)="clearFilters()">Clear</button>
        }
    </div>

    <!-- Current Cases Tab Content -->
    @if (activeTab === 'current') {
        <div class="current-cases-container">
            @if (filteredCurrentCases.length > 0) {
                <ng-container *ngTemplateOutlet="caseTable; context: { $implicit: filteredCurrentCases, showStatus: true }"></ng-container>
            } @else {
                <div class="card">
                    <app-empty-state [icon]="infoIcon" title="No Current Cases"
                        description="No active cases to display." [iconSize]="120">
                    </app-empty-state>
                </div>
            }
        </div>
    }

    <!-- Complete Cases Tab Content -->
    @if (activeTab === 'complete') {
        <div class="complete-cases-container">
            @if (filteredCompleteCases.length > 0) {
                <ng-container *ngTemplateOutlet="caseTable; context: { $implicit: filteredCompleteCases, showStatus: false }"></ng-container>
            } @else {
                <div class="card">
                    <app-empty-state [icon]="infoIcon" title="No Completed Cases"
                        description="No closed cases to display." [iconSize]="120">
                    </app-empty-state>
                </div>
            }
        </div>
    }
</div>

<!-- Reusable Case Table Template -->
<ng-template #caseTable let-cases let-showStatus="showStatus">
    <div class="table-wrapper">
        <table class="cases-table">
            <thead>
                <tr>
                    <th>Case Code</th>
                    <th>Client Name</th>
                    <th>Attorney</th>
                    <th>Hospital</th>
                    <th>Case Type</th>
                    <th>Lien Balance</th>
                    @if (showStatus) {
                        <th>Status</th>
                    } @else {
                        <th>Date</th>
                    }
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                @for (case of cases; track case.id) {
                <tr class="table-row">
                    <td class="case-code">{{ case.caseCode || case.id }}</td>
                    <td>{{ case.clientName }}</td>
                    <td>{{ case.attorney }}</td>
                    <td>{{ case.hospital }}</td>
                    <td>{{ case.caseType }}</td>
                    <td>{{ case.lienBalance | currency }}</td>
                    @if (showStatus) {
                        <td>
                            @if (getProgressStatus(case); as status) {
                                <span class="status-banner-inline" [ngClass]="status.variant">{{ status.label }}</span>
                            }
                        </td>
                    } @else {
                        <td class="date">{{ case.date | date: 'MMM d, yyyy' }}</td>
                    }
                    <td class="actions">
                        <button class="btn-icon" (click)="viewCaseDetails(case)"
                            [attr.aria-label]="'View case ' + (case.caseCode || case.id)">
                            <hugeicons-icon [icon]="viewIcon" [size]="18"></hugeicons-icon>
                        </button>
                    </td>
                </tr>
                }
            </tbody>
        </table>
    </div>
</ng-template>

<!-- Inquiry Selection Modal -->
<app-modal
    [isOpen]="showEnquiryModal"
    title="Select Accepted Inquiry"
    size="sm"
    [showCloseButton]="true"
    [showCancelButton]="true"
    [submitButtonText]="'Proceed'"
    [submitButtonDisabled]="!selectedEnquiry"
    (close)="closeModal()"
    (cancel)="closeModal()"
    (submit)="proceedWithCase()">
    @if (acceptedEnquiries.length > 0) {
        <div class="enquiry-hint">Select an inquiry to create a new case</div>
        <div class="enquiry-select-list">
            @for (enquiry of acceptedEnquiries; track enquiry.displayId) {
                <div class="enquiry-select-card"
                    [class.selected]="selectedEnquiry?.displayId === enquiry.displayId"
                    (click)="selectEnquiry(enquiry)">
                    <div class="enquiry-radio">
                        <div class="radio-ring">
                            @if (selectedEnquiry?.displayId === enquiry.displayId) {
                                <div class="radio-dot"></div>
                            }
                        </div>
                    </div>
                    <div class="enquiry-card-body">
                        <div class="enquiry-card-top">
                            <span class="enquiry-badge">#{{ enquiry.displayId }}</span>
                            <span class="enquiry-date">
                                <hugeicons-icon [icon]="calendarIcon" [size]="13"></hugeicons-icon>
                                {{ enquiry.displayDate | date:'mediumDate' }}
                            </span>
                        </div>
                        <div class="enquiry-card-main">{{ enquiry.client_name }}</div>
                        <div class="enquiry-card-sub">
                            <hugeicons-icon [icon]="userIcon" [size]="13"></hugeicons-icon>
                            {{ enquiry.attorney_name }}
                        </div>
                    </div>
                    @if (selectedEnquiry?.displayId === enquiry.displayId) {
                        <div class="enquiry-check">
                            <hugeicons-icon [icon]="checkIcon" [size]="18"></hugeicons-icon>
                        </div>
                    }
                </div>
            }
        </div>
    } @else {
        <div class="enquiry-empty">
            <p>No accepted inquiries found.</p>
        </div>
    }
</app-modal>
